import { request as umiRequest, getLocale } from "@umijs/max";
import type { RequestOptions } from "@@/plugin-request/request";

/**
 * @name request 二次封装
 * @description 统一处理请求头、Token 等配置
 */

// 默认请求配置
const defaultOptions: RequestOptions = {
  timeout: 6000000,
  headers: {
    "Content-Type": "application/json",
  },
};

/**
 * 过滤空值参数
 * @param params 请求参数
 */
const filterEmptyParams = (
  params: Record<string, any>,
): Record<string, any> => {
  if (!params) return {};
  const result: Record<string, any> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      result[key] = value;
    }
  });
  return result;
};

/**
 * 二次封装的请求工具
 * @param url 请求地址
 * @param options 请求配置
 */
const request = async <T = any>(url: string, options: RequestOptions = {}) => {
  const token = localStorage.getItem("token");

  const mergedOptions: RequestOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...(options.method === "POST"
        ? { "Content-Type": "application/json" }
        : {}),
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  if (token && mergedOptions.headers) {
    mergedOptions.headers.Authorization = `Bearer ${token}`;
  }

  // 添加语言请求头
  if (mergedOptions.headers) {
    mergedOptions.headers.Language = getLocale();
  }

  const finalUrl = `${API_URL}${url}`;

  // 过滤 GET 请求的 params 空值
  if (mergedOptions.method === "GET" && mergedOptions.params) {
    mergedOptions.params = filterEmptyParams(
      mergedOptions.params as Record<string, any>,
    );
  }

  // 过滤 POST/PUT 请求的 data 空值
  // if (
  //   (mergedOptions.method === "POST" || mergedOptions.method === "PUT") &&
  //   mergedOptions.data
  // ) {
  //   mergedOptions.data = filterEmptyParams(mergedOptions.data as Record<string, any>);
  // }

  try {
    const res = await umiRequest<T>(finalUrl, mergedOptions);

    // 如果是文件流请求，直接返回响应
    if (mergedOptions.responseType === "blob") {
      return res;
    }

    // 处理响应数据，根据实际情况调整
    const anyRes = res as any;
    // 兼容 0 和 200 为成功状态
    if (
      anyRes?.code === 0 ||
      anyRes?.code === 200 ||
      anyRes?.success === true
    ) {
      return anyRes.data;
    } else {
      const errorMsg =
        anyRes?.msg || anyRes?.message || anyRes?.errorMessage || "请求失败";
      const error: any = new Error(errorMsg);
      error.info = anyRes;
      throw error;
    }
  } catch (error: any) {
    // 如果已经由全局错误处理器处理过了（已展示过后端错误提示），则直接向上抛出
    // 调用方可通过 error.isGlobalHandled 判断是否需要展示前端错误提示
    if (error.isGlobalHandled) {
      throw error;
    }

    // 捕获 Axios 错误并提取后端返回的业务错误信息
    if (error.response && error.response.data) {
      const {
        msg,
        message: errorMessage,
        errorMessage: errorMsg,
      } = error.response.data;
      const realErrorMsg = msg || errorMessage || errorMsg || error.message;
      const wrappedError: any = new Error(realErrorMsg);
      wrappedError.isGlobalHandled = true;
      throw wrappedError;
    }
    throw error;
  }
};

// 方便使用常用的请求方法
request.get = <T = any>(url: string, options?: RequestOptions) =>
  request<T>(url, { ...options, method: "GET" });

request.post = <T = any>(url: string, data?: any, options?: RequestOptions) =>
  request<T>(url, { ...options, method: "POST", data });

request.put = <T = any>(url: string, data?: any, options?: RequestOptions) =>
  request<T>(url, { ...options, method: "PUT", data });

request.delete = <T = any>(url: string, options?: RequestOptions) =>
  request<T>(url, { ...options, method: "DELETE" });

export default request;
